import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Music, Eye, EyeOff, CheckCircle2 } from "lucide-react";

export default function AdminReset() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [form, setForm] = useState({ new_password: "", confirm: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.new_password !== form.confirm) {
      setError("La confirmation ne correspond pas au nouveau mot de passe.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: form.new_password });
      setDone(true);
    } catch (err) {
      setError(formatApiError(err, "Réinitialisation impossible. Redemandez un lien."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#FAF6F0] px-4">
      <div className="w-full max-w-md">
        <div className="rounded-2xl border border-stone-300 bg-white p-8 shadow-md sm:p-10">
          <div className="mb-8 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded bg-[#B83227] text-white">
              <Music className="h-5 w-5" aria-hidden="true" />
            </div>
            <div>
              <p className="font-display text-xl font-bold uppercase text-[#1E1B18]">Nouveau mot de passe</p>
              <p className="text-xs text-stone-500">Les Sonneurs de la Côte</p>
            </div>
          </div>

          {!token ? (
            <p data-testid="reset-no-token" className="rounded-lg bg-red-50 p-3 text-xs font-medium text-red-700">
              Lien incomplet : utilisez le lien complet reçu par e-mail.
            </p>
          ) : done ? (
            <div data-testid="reset-success" className="text-center">
              <CheckCircle2 className="mx-auto h-10 w-10 text-green-600" aria-hidden="true" />
              <p className="mt-4 text-sm text-stone-700">Votre mot de passe a été modifié.</p>
              <Link to="/admin" data-testid="reset-login-link" className="mt-4 inline-block rounded bg-[#B83227] px-6 py-3 text-xs font-bold uppercase tracking-wider text-white hover:bg-[#96281E]">
                Se connecter
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="reset-form" className="space-y-5">
              <div>
                <label htmlFor="reset-password" className="mb-2 block text-xs font-bold uppercase text-stone-700">Nouveau mot de passe (8 caractères minimum)</label>
                <div className="relative">
                  <input
                    id="reset-password"
                    data-testid="reset-password-input"
                    type={showPassword ? "text" : "password"}
                    minLength="8"
                    value={form.new_password}
                    onChange={(e) => setForm({ ...form, new_password: e.target.value })}
                    required
                    className="w-full rounded-lg border border-stone-300 bg-[#FAF6F0] p-3.5 pr-12 text-sm focus:border-[#B83227] focus:outline-none"
                  />
                  <button
                    type="button"
                    data-testid="toggle-reset-visibility"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 rounded p-1 text-stone-400 hover:text-stone-700"
                    aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>
              <div>
                <label htmlFor="reset-confirm" className="mb-2 block text-xs font-bold uppercase text-stone-700">Confirmer le mot de passe</label>
                <input
                  id="reset-confirm"
                  data-testid="reset-confirm-input"
                  type={showPassword ? "text" : "password"}
                  minLength="8"
                  value={form.confirm}
                  onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                  required
                  className="w-full rounded-lg border border-stone-300 bg-[#FAF6F0] p-3.5 text-sm focus:border-[#B83227] focus:outline-none"
                />
              </div>
              {error && (
                <p data-testid="reset-error" className="rounded-lg bg-red-50 p-3 text-xs font-medium text-red-700">{error}</p>
              )}
              <button
                type="submit"
                data-testid="reset-submit"
                disabled={loading}
                className="w-full rounded bg-[#B83227] px-6 py-3.5 text-xs font-bold uppercase tracking-wider text-white shadow transition-colors duration-300 hover:bg-[#96281E] disabled:opacity-60"
              >
                {loading ? "Enregistrement..." : "Enregistrer le nouveau mot de passe"}
              </button>
            </form>
          )}
        </div>
        <p className="mt-6 text-center">
          <Link to="/admin" className="text-xs font-bold uppercase text-stone-500 hover:text-[#B83227]">
            &larr; Retour à la connexion
          </Link>
        </p>
      </div>
    </div>
  );
}
